/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { View } from "react-native";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

import {
  skaleWhite,
  skaleWebBorderGray,
  colorWithOpacity,
  upduoMediumLightGray,
  upduoLightestGray,
} from "config/UpduoColors";
import { formattedDateString, getGraphColor } from "config/Utilities";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface GraphPlotProps {
  dayRangeStart: Date;
  dayRangeEnd: Date;
  timeSeriesData: any[];
  fullDataQueryType: string;
  selectedPlotIdArray: string[];
}

function getPlot({
  dayRangeStart,
  dayRangeEnd,
  selectedPlotIdArray,
  fullDataQueryType,
  timeSeriesData,
  todayDate,
}: GraphPlotProps & { todayDate: Date }) {
  const millisecondsInDay = 1000 * 60 * 60 * 24;
  const isWithinOneDay =
    dayRangeEnd.getTime() - dayRangeStart.getTime() < millisecondsInDay;
  if (isWithinOneDay) {
    dayRangeStart.setDate(dayRangeStart.getDate() - 1);
  }
  const numberOfDates =
    Math.floor(
      (dayRangeEnd.getTime() - dayRangeStart.getTime()) / millisecondsInDay
    ) + 1; // adding 1 to include endpoint date
  const dateFormat = new Intl.DateTimeFormat("en-us", {
    month: "short",
    day: "numeric",
  });
  const dateStringArray: string[] = [];
  const displayDateArray: string[] = [];
  for (let k = 0; k < numberOfDates; k++) {
    const countNextDate = new Date(
      dayRangeStart.getTime() + k * millisecondsInDay
    );
    dateStringArray.push(formattedDateString(countNextDate));
    displayDateArray.push(dateFormat.format(countNextDate));
  }

  const plotData =
    selectedPlotIdArray.length > 0
      ? getPlotData({
          dateStringArray,
          dataSeries: timeSeriesData[fullDataQueryType],
          selectedPlotIdArray,
          isLatest:
            formattedDateString(todayDate) === formattedDateString(dayRangeEnd),
        })
      : [];

  return {
    labels: displayDateArray,
    datasets: plotData,
  };
}
function getPlotData({
  dateStringArray,
  dataSeries,
  selectedPlotIdArray,
  isLatest,
}: {
  dateStringArray: string[];
  dataSeries: any;
  selectedPlotIdArray: string[];
  isLatest: boolean;
}) {
  const dataSets = dataSeries.data_sets;
  const numberOfDates = dateStringArray.length;
  const _plotData: any[] = [];
  for (let i = 0; i < dataSets.length; i++) {
    if (!selectedPlotIdArray.includes(dataSets[i].id)) {
      continue;
    }
    if (numberOfDates > 1) {
      const dates = new Array(numberOfDates).fill(0);
      for (let j = 0; j < dataSets[i].data.length; j++) {
        const workingDateString = dataSets[i].data[j].date.split("T")[0]; // Only look at day, not time
        const dateIndex = dateStringArray.indexOf(workingDateString);
        if (dateIndex >= 0) {
          dates[dateIndex] = dataSets[i].data[j].value;
        }
      }
      _plotData.push(
        ...createSafeDataSetObject(
          isLatest,
          dates,
          getGraphColor(dataSets[i].name, i),
          dataSets[i].name
        )
      );
    }
  }
  return _plotData;
}

function createSafeDataSetObject(
  isLatest: boolean,
  data: any[],
  dataColor: string,
  dataLabel: string,
  isFilled: boolean = false,
  isBottom: boolean = false
) {
  const isLastSegment = (index: number) => {
    return index === data.length - 1;
  };

  let dataObjectMain = {
    lineTension: 0.5,
    borderWidth: 1,
    hoverBorderWidth: 10,
    borderColor: dataColor,
    pointBorderColor: (point: any) => {
      return isLatest && isLastSegment(point.dataIndex)
        ? colorWithOpacity(dataColor, 0.25)
        : dataColor;
    },
    pointBackgroundColor: (point: any) => {
      return isLatest && isLastSegment(point.dataIndex)
        ? colorWithOpacity(dataColor, 0.5)
        : dataColor;
    },
    backgroundColor: (item: any) => {
      if (!isFilled) {
        return "transparent";
      }
      if (!isLastSegment(item.dataIndex)) {
        return colorWithOpacity(dataColor, 0.5);
      }
      return isLatest
        ? colorWithOpacity(dataColor, 0.25)
        : colorWithOpacity(dataColor, 0.5);
    },
    data: data ? data : null,
    fill: isBottom ? "origin" : "-1",
    segment: {
      borderColor: (ctx: any) => {
        if (isLatest && ctx.p0DataIndex === data.length - 2) {
          return colorWithOpacity(dataColor, 0.5);
        }
      },
      borderDash: (ctx: any) => {
        if (isLatest && ctx.p0DataIndex === data.length - 2) {
          return [10, 5];
        }
      },
    },
  };

  if (dataLabel) {
    dataObjectMain = Object.assign({}, dataObjectMain, { label: dataLabel });
  }

  return [dataObjectMain];
}

function GraphPlot({
  dayRangeStart,
  dayRangeEnd,
  timeSeriesData,
  fullDataQueryType,
  selectedPlotIdArray,
}: GraphPlotProps) {
  const todayDate = new Date();

  const data = getPlot({
    dayRangeStart,
    dayRangeEnd,
    timeSeriesData,
    fullDataQueryType,
    selectedPlotIdArray,
    todayDate,
  });

  let maxValue = 0;
  data.datasets.forEach((item) => {
    const curMaxValue = Math.max(...item.data);
    maxValue = Math.max(maxValue, curMaxValue);
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          padding: 10,
          color: upduoMediumLightGray,
          font: { family: "ObjectSans-Regular" },
        },
      },
      y: {
        max: maxValue + maxValue / 10,
        grid: {
          drawBorder: false,
          color: upduoLightestGray,
          borderDash: [5, 5],
        },
        ticks: {
          padding: 10,
          color: upduoMediumLightGray,
          font: { family: "ObjectSans-Regular" },
          beginAtZero: true,
          callback: function (value: any) {
            if (Number.isInteger(value)) {
              return value;
            }
          },
        },
        title: {
          display: true,
          text: timeSeriesData[fullDataQueryType]
            ? timeSeriesData[fullDataQueryType].data_description
            : "",
          color: upduoMediumLightGray,
          font: { family: "ObjectSans-Regular" },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        intersect: false,
        titleFont: { family: "ObjectSans-Regular" },
        bodyFont: { family: "ObjectSans-Regular" },
      },
    },
  };

  return (
    <View
      style={{
        flex: 3,
        height: 450,
        borderWidth: 1,
        borderColor: skaleWebBorderGray,
        backgroundColor: skaleWhite,
        zIndex: -10,
        padding: 20,
      }}
    >
      <Line data={data} options={chartOptions} />
    </View>
  );
}

export default React.memo(GraphPlot);
